import React, { useMemo, useCallback } from 'react';
import { useWorkflowDelete, useWorkflowList } from '../hooks/use-workflow.hook';
import type { ColumnConfig } from '../../../../common/PaginatedTable';
import type { Workflow } from '../types/workflow.types';
import WorkflowTable from '../components/workflow-table.component';
import { DEFAULT_PAGINATED_TABLE_DATA } from '../../../../../const/default-paginated-table-data.const';
import ActionButtons from '../../../../common/ActionButtons';
import { useFilters } from '../../../../../hooks/use-filters.hook';
import { message } from 'antd';
import HeaderRow from '../../../../common/HeaderRow';
import { useTranslation } from 'react-i18next';
import { useAuthUser } from '../../../../../states/user.atom';
import { rbac } from 'permissions/permissions';
import { WithPermissionCheck } from '../../../../auth/WithPermissionCheck';
import { extractCreateError } from '@services/extract-api-error.service';

const WorkflowTableContainer = () => {
  const { t } = useTranslation();
  const { getUserPermissions } = useAuthUser();
  const userPermissions = getUserPermissions();

  const {
    queryParams,
    handlePageChange,
    handlePageSizeChange,
    handleFilterChange,
    handleSearchChange,
    handleSortChange,
  } = useFilters();

  const { data, isLoading, isFetching } = useWorkflowList(queryParams);
  const { docs: tableData = [] } = data ?? DEFAULT_PAGINATED_TABLE_DATA;

  const deleteWorkflowMutation = useWorkflowDelete();

  const handleDeleteWorkflow = useCallback(
    async (record: Workflow) => {
      try {
        await deleteWorkflowMutation.mutateAsync(record.id);
        message.success(t('crud.delete.success', { entity: 'Workflow' }));
      } catch (error) {
        const errorMessage = extractCreateError(
          error,
          t('crud.delete.error', { entity: 'Workflow' })
        );
        message.error(errorMessage);
      }
    },
    [deleteWorkflowMutation, t]
  );

  // Handle table sorting
  const handleTableChange = useCallback(
    (pagination: any, filters: any, sorter: any) => {
      if (sorter && sorter.field) {
        let sortOrder: 'asc' | 'desc';

        if (sorter.order === 'ascend') {
          sortOrder = 'asc';
        } else if (sorter.order === 'descend') {
          sortOrder = 'desc';
        } else {
          sortOrder = 'asc';
        }

        handleSortChange(sorter.field as string, sortOrder);
      } else {
        handleSortChange('createdAt', 'desc');
      }
    },
    [handleSortChange]
  );

  const columns = useMemo<ColumnConfig<Workflow>[]>(
    () => [
      {
        title: 'Workflow Name',
        key: 'name',
        dataIndex: 'name',
        sorter: true,
      },
      {
        title: t('common.actions'),
        key: 'actions',
        align: 'right',
        render: (_: any, record: Workflow) => (
          <ActionButtons
            showEdit={rbac.hasAccess(userPermissions, 'workflow.update')}
            showDelete={rbac.hasAccess(userPermissions, 'workflow.delete')}
            showView={rbac.hasAccess(userPermissions, 'workflow.view')}
            record={record}
            baseUrl="/dashboard/workflows"
            onDelete={handleDeleteWorkflow}
          />
        ),
      },
    ],
    [t, userPermissions, handleDeleteWorkflow]
  );

  return (
    <>
      <HeaderRow
        title="Workflows"
        showCreateButton={rbac.hasAccess(userPermissions, 'workflow.create')}
        createRoute="/dashboard/workflows/create"
        showBackButton={false}
      />

      <div className="row mt-3">
        <WorkflowTable
          data={tableData}
          columns={columns}
          loading={isLoading || isFetching}
          pagination={data}
          rowKey="id"
          showSizeChanger={true}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          showSearchBar={true}
          onSearchChange={handleSearchChange}
          showFilters={false}
          availableFilters={[]}
          onFilterChange={handleFilterChange}
          tableProps={{
            onChange: handleTableChange,
            sortDirections: ['ascend', 'descend'],
          }}
          sortField={queryParams.sortField}
          sortOrder={queryParams.sortOrder}
        />
      </div>
    </>
  );
};

export default WithPermissionCheck(WorkflowTableContainer, 'workflow.view');
